import React, { ComponentClass } from 'react';
import { Container } from '@uds/grid';
import PaddingBox from '@uds/padding-box';
import { Heading02, Heading04 } from '@uds/typography';
import { useBreakpoints } from '@ux/responsive';
import Shortcode, { ShortcodeProps } from 'components/Shortcode';
import visibilitySet from 'presentation/hocs/visibilitySet';
import errorBoundary from 'presentation/hocs/errorBoundary';
import { RibbonProps, Type } from 'domain/constants/ribbons';
import { Alignment } from 'domain/constants/alignment';
import { isNilOrEmpty } from 'presentation/utils/generalUtils';
import Ribbon from 'components/ribbons/Ribbon';
import findResponsiveItem from 'presentation/utils/findResponsiveItem';
import responsiveColumns from 'presentation/utils/responsiveColumns';
import { ResponsiveSet } from 'core/ribbons/primitives';
import { ColumnCount } from 'domain/constants/layout';

import getClassNames, { StyledGrid } from './Aspects.stylesheet';
import Aspect, { AspectType } from './Aspect';

export interface OuterProps extends RibbonProps {
  aspects: Array<AspectType>;
  text: {
    title: ShortcodeProps;
    body: ShortcodeProps;
  };
  ticks: boolean;
  hBaseline?: number;
}

export const PureAspects = ({
  id,
  colorway,
  loading,
  hBaseline = 1,
  text,
  aspects,
  ticks,
  layout,
  name,
}: OuterProps) => {
  const { body, title } = text;

  const hasBody = !isNilOrEmpty(body);
  const hasTitle = !isNilOrEmpty(title);
  const hasHeader = hasTitle || hasBody;

  const alignment = findResponsiveItem(useBreakpoints(), [
    layout?.alignmentMobile ?? Alignment.STARTING,
    layout?.alignmentTablet ?? Alignment.STARTING,
    layout?.alignment ?? Alignment.STARTING,
  ]);

  // Adding index to aspects array to avoid performance issues when passing keys as index to render
  const aspectsWithIndex = aspects.map((item, index) => ({
    ...item,
    index: `Aspect${index}${id}`,
  }));

  const columns = responsiveColumns([1, 2, 4], layout.columns as ResponsiveSet<ColumnCount>);

  const classNames = getClassNames();
  return (
    <Ribbon id={id} colorway={colorway} loading={loading} layout={layout} name={name} type={Type.ASPECTS}>
      <div className={classNames.root} data-alignment={alignment}>
        <Container>
          <StyledGrid columns={columns as [number, number, number]} aspects={aspects} data-no-header={!hasHeader}>
            {hasHeader && (
              <PaddingBox className={classNames.header}>
                <Heading02 depth={hBaseline}>
                  <Shortcode against={colorway} code={text.title} />
                </Heading02>
                {hasBody && (
                  <Heading04 component="p" className={classNames.body}>
                    <Shortcode code={text.body} against={colorway} />
                  </Heading04>
                )}
              </PaddingBox>
            )}
            {aspectsWithIndex.map((aspect) => (
              <Aspect
                key={aspect.index}
                hBaseline={hBaseline + 1}
                title={aspect.title}
                body={aspect.body}
                colorway={colorway}
                alignment={alignment}
                ticks={ticks}
              />
            ))}
          </StyledGrid>
        </Container>
      </div>
    </Ribbon>
  );
};

PureAspects.displayName = 'Aspects';

export default errorBoundary(visibilitySet(PureAspects)) as ComponentClass<OuterProps>;
